// components/EvaluationTimeline.jsx
import React, { useState, useRef, useMemo } from 'react';

// ─── Dashboard palette ────────────────────────────────────────────────────────
const C = {
  bg:     '#08101E',
  bg1:    '#0C1525',
  bg2:    '#101B2E',
  bg3:    '#152030',
  border: '#1E2D42',
  text:   '#E8EDF5',
  body:   '#B8C8DC',
  meta:   '#6A7D95',
  cyan:   '#00C8FF',
  green:  '#00E896',
  amber:  '#FFB340',
  red:    '#FF4466',
  blue:   '#4488FF',
  purple: '#9966FF',
  font:   "'DM Mono', monospace",
};

const getDecisionColor = (decision) => {
  switch (decision) {
    case 'PROCEED': return C.green;
    case 'SKIP':    return C.meta;
    case 'ADJUST':  return C.amber;
    case 'HOLD':    return C.cyan;
    case 'CLOSE':   return C.red;
    default:        return C.meta;
  }
};

const getDecisionIcon = (decision) => {
  switch (decision) {
    case 'PROCEED': return '✓';
    case 'SKIP':    return '⊗';
    case 'ADJUST':  return '△';
    case 'HOLD':    return '⏸';
    case 'CLOSE':   return '✕';
    default:        return '·';
  }
};

const getPhaseColor = (phase) => phase === 'PROACTIVE' ? C.meta : C.blue;

// ─── Tooltip ──────────────────────────────────────────────────────────────────
const Tip = ({ x, y, children }) => (
  <div style={{
    position: 'fixed', left: x + 15, top: y + 15,
    background: C.bg1, border: `1px solid ${C.border}`,
    borderRadius: 8, padding: '10px 14px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
    zIndex: 1000, pointerEvents: 'none',
    maxWidth: 300, fontFamily: C.font,
  }}>
    {children}
  </div>
);

// ─── Filter button ────────────────────────────────────────────────────────────
const FilterBtn = ({ active, color, onClick, children }) => (
  <button onClick={onClick} style={{
    fontFamily: C.font, fontSize: 10, letterSpacing: '0.05em',
    background:   active ? C.bg2 : 'transparent',
    color:        active ? (color || C.text) : C.meta,
    border:       `1px solid ${active ? (color || C.cyan) : C.border}`,
    borderRadius: 4, padding: '2px 10px', cursor: 'pointer',
  }}>
    {children}
  </button>
);

// ─── Main component ───────────────────────────────────────────────────────────
const EvaluationTimeline = ({
  predictions, onEvaluationSelect, selectedEvalId,
  positions, onPositionSelect,
}) => {
  const timelineRef = useRef(null);
  const [hoveredEval,     setHoveredEval]     = useState(null);
  const [hoveredPosition, setHoveredPosition] = useState(null);
  const [mouse,           setMouse]           = useState({ x: 0, y: 0 });
  const [isDragging,      setIsDragging]      = useState(false);
  const [dragStart,       setDragStart]       = useState(0);
  const [scrollLeft,      setScrollLeft]      = useState(0);
  const [filterType,      setFilterType]      = useState('all');
  const [filterDecision,  setFilterDecision]  = useState('all');

  // ── Data ────────────────────────────────────────────────────────────────────
  const allEvaluations = useMemo(() => {
    if (!predictions?.length) return [];
    return predictions.map((bar, index) => {
      const decision = bar.eval_decision || bar.decision || bar.signal;
      if (!decision) return null;
      const reasoning = bar.eval_reasoning || bar.eval_narrative_summary ||
        bar.reasoning || bar.gen_ai_msg?.Reason || '';
      if (!reasoning?.trim()) return null;
      let evalType = bar.eval_type;
      if (!evalType || evalType === 'UNKNOWN') {
        evalType = (bar.eval_position_id || bar.position_id) ? 'REACTIVE' : 'PROACTIVE';
      }
      return {
        id: `eval-${index}-${bar.t}`, type: evalType, decision, reasoning,
        timestamp: bar.t || bar.timestamp,
        confidence: bar.eval_confidence || bar.confidence,
        execution_time: bar.eval_execution_time,
        position_id: bar.eval_position_id || bar.position_id,
        exit_risk: bar.eval_exit_risk, fullData: bar,
      };
    }).filter(Boolean);
  }, [predictions]);

  const positionMarkers = useMemo(() => {
    if (!positions?.length) return [];
    return positions.map(pos => ({
      id: pos.id || pos.a_id || `pos-${pos.group}`,
      entryTime: pos.entryTime, exitTime: pos.exitTime,
      entryPrice: pos.entryPrice, exitPrice: pos.exitPrice || pos.currentPrice,
      symbol: pos.symbol, isLong: pos.isLong, qty: pos.qty,
      pnl: pos.unrealizedPL || pos.pl_delta || 0,
      isWin: pos.isWin, isClosed: !!pos.exitTime, position: pos,
    }));
  }, [positions]);

  const stats = useMemo(() => {
    const decisions = {};
    allEvaluations.forEach(e => { decisions[e.decision] = (decisions[e.decision] || 0) + 1; });
    return {
      total:     allEvaluations.length,
      proactive: allEvaluations.filter(e => e.type === 'PROACTIVE').length,
      reactive:  allEvaluations.filter(e => e.type === 'REACTIVE').length,
      decisions,
    };
  }, [allEvaluations]);

  const filteredEvaluations = useMemo(() =>
    allEvaluations.filter(e => {
      if (filterType !== 'all' && e.type.toLowerCase() !== filterType) return false;
      if (filterDecision !== 'all' && e.decision !== filterDecision) return false;
      return true;
    }), [allEvaluations, filterType, filterDecision]);

  const entryPoint         = useMemo(() => allEvaluations.find(e => e.type === 'REACTIVE'), [allEvaluations]);
  const availableDecisions = useMemo(() => Object.keys(stats.decisions).sort(), [stats.decisions]);

  const startTime     = allEvaluations.length > 0
    ? new Date(filteredEvaluations[0]?.timestamp || allEvaluations[0].timestamp) : new Date();
  const endTime       = allEvaluations.length > 0
    ? new Date(filteredEvaluations[filteredEvaluations.length - 1]?.timestamp || allEvaluations[allEvaluations.length - 1].timestamp) : new Date();
  const totalDuration = endTime - startTime || 1;

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart(e.pageX - timelineRef.current.offsetLeft);
    setScrollLeft(timelineRef.current.scrollLeft);
  };
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    timelineRef.current.scrollLeft = scrollLeft - (e.pageX - timelineRef.current.offsetLeft - dragStart) * 2;
  };
  const handleMouseUp = () => setIsDragging(false);

  const formatTime = ts  => new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  const getPct     = ts  => ((new Date(ts) - startTime) / totalDuration) * 100;
  const inView     = ts  => { const t = new Date(ts); return t >= startTime && t <= endTime; };

  // ── Empty state ──────────────────────────────────────────────────────────────
  if (!allEvaluations.length) {
    return (
      <div style={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 24px', marginBottom: 12 }}>
        <div style={{ fontFamily: C.font, fontSize: 10, color: C.meta, textAlign: 'center' }}>
          No evaluations available. Evaluations appear during market hours.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: C.bg1, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: '16px 20px', marginBottom: 12,
      fontFamily: C.font,
    }}>

      {/* Header + filters */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 9, color: C.meta, letterSpacing: '1.2px', marginBottom: 3 }}>
            EVALUATION TIMELINE · SESSION
          </div>
          <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>
            Agent Decision History
          </div>
          <div style={{ fontSize: 10, color: C.meta, marginTop: 2, marginBottom: 4 }}>
            {formatTime(startTime)} — {formatTime(endTime)}
          </div>
          <div style={{ fontSize: 10, color: C.body, opacity: 0.8, lineHeight: 1.6, maxWidth: 480 }}>
            Every agent decision, captured by the minute.{' '}
            <span style={{ color: C.cyan }}>Click any circle</span> to see full reasoning.{' '}
            <span style={{ color: C.green }}>Click a position bar</span> to review the trade.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 9, color: C.meta, marginRight: 2 }}>TYPE</span>
          {[
            { key: 'all',       label: `All · ${stats.total}` },
            { key: 'proactive', label: `Proactive · ${stats.proactive}` },
            { key: 'reactive',  label: `Reactive · ${stats.reactive}` },
          ].map(({ key, label }) => (
            <FilterBtn key={key} active={filterType === key} onClick={() => setFilterType(key)}>{label}</FilterBtn>
          ))}

          <div style={{ width: 1, height: 16, background: C.border, margin: '0 6px' }} />

          <span style={{ fontSize: 9, color: C.meta, marginRight: 2 }}>DECISION</span>
          <FilterBtn active={filterDecision === 'all'} onClick={() => setFilterDecision('all')}>All</FilterBtn>
          {availableDecisions.map(dec => (
            <FilterBtn key={dec} active={filterDecision === dec} color={getDecisionColor(dec)} onClick={() => setFilterDecision(dec)}>
              {getDecisionIcon(dec)} {dec} · {stats.decisions[dec]}
            </FilterBtn>
          ))}
        </div>
      </div>

      <div style={{ height: 1, background: C.border, marginBottom: 14, opacity: 0.5 }} />

      {/* Scrollable two-lane timeline */}
      <div
        ref={timelineRef}
        style={{
          overflowX: 'auto', overflowY: 'visible',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none', paddingTop: 12, paddingBottom: 12,
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Lane labels column + timeline column */}
        <div style={{ display: 'flex', minWidth: Math.max(1000, filteredEvaluations.length * 80) }}>

          {/* Lane labels (fixed left edge) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, flexShrink: 0, width: 72, marginRight: 8 }}>
            {/* time axis label spacer */}
            <div style={{ height: 20 }} />
            {/* POSITIONS lane label */}
            <div style={{
              height: 36, display: 'flex', alignItems: 'center',
              fontSize: 8, color: C.meta, letterSpacing: '0.8px',
              borderRight: `1px solid ${C.border}`, paddingRight: 6,
              justifyContent: 'flex-end',
            }}>
              POSITIONS
            </div>
            {/* divider spacer */}
            <div style={{ height: 4 }} />
            {/* EVALS lane label */}
            <div style={{
              height: 56, display: 'flex', alignItems: 'center',
              fontSize: 8, color: C.meta, letterSpacing: '0.8px',
              borderRight: `1px solid ${C.border}`, paddingRight: 6,
              justifyContent: 'flex-end',
            }}>
              EVALS
            </div>
          </div>

          {/* Timeline tracks */}
          <div style={{ flex: 1, position: 'relative' }}>

            {/* Time axis row */}
            <div style={{ position: 'relative', height: 20 }}>
              {(() => {
                const marks = [];
                const iv = 5 * 60 * 1000;
                let cur = new Date(Math.ceil(startTime.getTime() / iv) * iv);
                while (cur <= endTime) {
                  const pct = getPct(cur);
                  marks.push(
                    <div key={cur.getTime()} style={{ position: 'absolute', left: `${pct}%`, top: 0, height: '100%', borderLeft: `1px dashed ${C.border}22` }}>
                      <div style={{ position: 'absolute', top: 2, left: -18, fontSize: 8, color: C.meta, whiteSpace: 'nowrap' }}>
                        {formatTime(cur)}
                      </div>
                    </div>
                  );
                  cur = new Date(cur.getTime() + iv);
                }
                return marks;
              })()}
            </div>

            {/* POSITIONS lane */}
            <div style={{
              position: 'relative', height: 36,
              background: C.bg, borderRadius: 6,
              border: `1px solid ${C.border}`,
            }}>
              {/* Gridlines */}
              {(() => {
                const marks = [];
                const iv = 5 * 60 * 1000;
                let cur = new Date(Math.ceil(startTime.getTime() / iv) * iv);
                while (cur <= endTime) {
                  marks.push(
                    <div key={cur.getTime()} style={{ position: 'absolute', left: `${getPct(cur)}%`, top: 0, height: '100%', borderLeft: `1px dashed ${C.border}` }} />
                  );
                  cur = new Date(cur.getTime() + iv);
                }
                return marks;
              })()}

              {/* Position pill bars */}
              {positionMarkers.map(marker => {
                if (!inView(marker.entryTime)) return null;
                const ep   = getPct(marker.entryTime);
                const xp   = marker.exitTime ? getPct(marker.exitTime) : Math.min(ep + 2, 99);
                const col  = marker.isWin ? C.green : C.red;
                const w    = Math.max(xp - ep, 1.5);
                const pnlStr = marker.pnl != null ? ` ${marker.pnl >= 0 ? '+' : ''}$${Math.abs(marker.pnl).toFixed(0)}` : '';
                const label  = `${marker.isLong ? 'LONG' : 'SHORT'}${pnlStr}`;

                return (
                  <div
                    key={marker.id}
                    onClick={e => { e.stopPropagation(); onPositionSelect?.(marker.position); }}
                    onMouseEnter={e => { setHoveredPosition(marker); setMouse({ x: e.clientX, y: e.clientY }); }}
                    onMouseLeave={() => setHoveredPosition(null)}
                    style={{
                      position: 'absolute',
                      left: `${ep}%`, top: '50%',
                      width: `${w}%`,
                      height: 20,
                      transform: 'translateY(-50%)',
                      background: col,
                      opacity: marker.isClosed ? 0.85 : 0.5,
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center',
                      paddingLeft: 6, overflow: 'hidden',
                      boxShadow: `0 0 8px ${col}50`,
                      border: `1px solid ${col}`,
                      zIndex: 10,
                    }}
                  >
                    {w > 6 && (
                      <span style={{
                        fontSize: 8, fontFamily: C.font,
                        color: '#000', fontWeight: 600,
                        whiteSpace: 'nowrap', letterSpacing: '0.3px',
                      }}>
                        {label}
                      </span>
                    )}
                  </div>
                );
              })}

              {/* Entry dot on positions lane */}
              {positionMarkers.map(marker => {
                if (!inView(marker.entryTime)) return null;
                const ep  = getPct(marker.entryTime);
                const col = marker.isLong ? C.green : C.red;
                return (
                  <div key={`entry-dot-${marker.id}`} style={{
                    position: 'absolute', left: `${ep}%`, top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 8, height: 8, borderRadius: '50%',
                    background: col, border: `2px solid ${C.bg1}`,
                    zIndex: 20, pointerEvents: 'none',
                  }} />
                );
              })}
            </div>

            {/* Lane divider */}
            <div style={{ height: 4 }} />

            {/* EVALS lane */}
            <div style={{
              position: 'relative', height: 56,
              background: C.bg, borderRadius: 6,
              border: `1px solid ${C.border}`,
              borderBottom: `2px solid ${C.border}`,
            }}>
              {/* Gridlines */}
              {(() => {
                const marks = [];
                const iv = 5 * 60 * 1000;
                let cur = new Date(Math.ceil(startTime.getTime() / iv) * iv);
                while (cur <= endTime) {
                  marks.push(
                    <div key={cur.getTime()} style={{ position: 'absolute', left: `${getPct(cur)}%`, top: 0, height: '100%', borderLeft: `1px dashed ${C.border}` }} />
                  );
                  cur = new Date(cur.getTime() + iv);
                }
                return marks;
              })()}

              {/* ENTRY separator — spans both lanes via absolute on outer container */}
              {entryPoint && (filterType === 'all' || filterType === 'reactive') && (
                <div style={{ position: 'absolute', left: `${getPct(entryPoint.timestamp)}%`, top: -4, height: 'calc(100% + 4px)', borderLeft: `2px solid ${C.amber}`, zIndex: 15 }}>
                  <div style={{ position: 'absolute', top: 2, left: 4, fontSize: 8, color: C.amber, background: C.bg1, border: `1px solid ${C.amber}50`, padding: '1px 5px', borderRadius: 3, whiteSpace: 'nowrap' }}>
                    ENTRY
                  </div>
                </div>
              )}

              {/* Eval circles */}
              {filteredEvaluations.map(ev => {
                const pct   = getPct(ev.timestamp);
                const isSel = selectedEvalId === ev.id;
                const dCol  = getDecisionColor(ev.decision);
                const pCol  = getPhaseColor(ev.type);
                return (
                  <div
                    key={ev.id}
                    style={{ position: 'absolute', left: `${pct}%`, top: '50%', transform: 'translate(-50%, -50%)', cursor: 'pointer', zIndex: isSel ? 20 : 10 }}
                    onClick={e => { e.stopPropagation(); onEvaluationSelect?.(ev); }}
                    onMouseEnter={e => { setHoveredEval(ev); setMouse({ x: e.clientX, y: e.clientY }); }}
                    onMouseLeave={() => setHoveredEval(null)}
                  >
                    <div style={{
                      width: isSel ? 20 : 13, height: isSel ? 20 : 13,
                      borderRadius: '50%', background: pCol,
                      border: `2.5px solid ${dCol}`,
                      boxShadow: isSel ? `0 0 10px ${dCol}80` : 'none',
                      transition: 'all 0.15s',
                    }} />
                    <div style={{
                      position: 'absolute', bottom: -16, left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: 9, color: dCol, fontWeight: 600,
                    }}>
                      {getDecisionIcon(ev.decision)}
                    </div>
                  </div>
                );
              })}
            </div>

          </div>{/* end timeline tracks */}
        </div>{/* end two-lane flex */}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
        {/* Position lane */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 24, height: 10, borderRadius: 3, background: C.green, boxShadow: `0 0 6px ${C.green}50` }} />
          <span style={{ fontSize: 9, color: C.meta }}>Win position</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 24, height: 10, borderRadius: 3, background: C.red, boxShadow: `0 0 6px ${C.red}50` }} />
          <span style={{ fontSize: 9, color: C.meta }}>Loss position</span>
        </div>

        <div style={{ width: 1, height: 12, background: C.border }} />

        {/* Eval lane */}
        {[{ color: C.meta, label: 'Proactive eval' }, { color: C.blue, label: 'Reactive eval' }].map(({ color, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
            <span style={{ fontSize: 9, color: C.meta }}>{label}</span>
          </div>
        ))}

        <div style={{ width: 1, height: 12, background: C.border }} />

        {['PROCEED','SKIP','ADJUST','HOLD','CLOSE'].map(dec => (
          <span key={dec} style={{ fontSize: 9, color: getDecisionColor(dec) }}>
            {getDecisionIcon(dec)} {dec}
          </span>
        ))}
      </div>

      {/* Eval tooltip */}
      {hoveredEval && !isDragging && (
        <Tip x={mouse.x} y={mouse.y}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
            {[
              { text: hoveredEval.type,     col: getPhaseColor(hoveredEval.type) },
              { text: `${getDecisionIcon(hoveredEval.decision)} ${hoveredEval.decision}`, col: getDecisionColor(hoveredEval.decision) },
            ].map(({ text, col }) => (
              <span key={text} style={{ fontSize: 9, color: col, background: C.bg2, border: `1px solid ${col}40`, padding: '1px 6px', borderRadius: 3 }}>{text}</span>
            ))}
          </div>
          <div style={{ fontSize: 9, color: C.meta, marginBottom: 6 }}>
            {formatTime(hoveredEval.timestamp)}{hoveredEval.confidence ? ` · Conf ${hoveredEval.confidence}%` : ''}
          </div>
          <div style={{ fontSize: 10, color: C.body, lineHeight: 1.6 }}>
            {hoveredEval.reasoning.substring(0, 130)}{hoveredEval.reasoning.length > 130 ? '…' : ''}
          </div>
          {hoveredEval.exit_risk && (
            <div style={{ marginTop: 6, fontSize: 9, color: hoveredEval.exit_risk.risk_level === 'LOW' ? C.green : hoveredEval.exit_risk.risk_level === 'MED' ? C.amber : C.red }}>
              Risk: {hoveredEval.exit_risk.risk_level}
            </div>
          )}
          <div style={{ fontSize: 9, color: C.meta, marginTop: 6, opacity: 0.7 }}>Click to view full details</div>
        </Tip>
      )}

      {/* Position tooltip */}
      {hoveredPosition && !isDragging && (
        <Tip x={mouse.x} y={mouse.y}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
            {[
              { text: hoveredPosition.isLong ? 'LONG' : 'SHORT', col: hoveredPosition.isLong ? C.green : C.red },
              { text: hoveredPosition.isWin  ? 'WIN'  : 'LOSS',  col: hoveredPosition.isWin  ? C.green : C.red },
            ].map(({ text, col }) => (
              <span key={text} style={{ fontSize: 9, color: col, background: C.bg2, border: `1px solid ${col}40`, padding: '1px 6px', borderRadius: 3 }}>{text}</span>
            ))}
            <span style={{ fontSize: 11, color: C.text, fontWeight: 500 }}>{hoveredPosition.symbol}</span>
          </div>
          {[
            { label: 'Entry', val: `$${hoveredPosition.entryPrice?.toFixed(2)} @ ${new Date(hoveredPosition.entryTime).toLocaleTimeString()}` },
            hoveredPosition.exitTime ? { label: 'Exit', val: `$${hoveredPosition.exitPrice?.toFixed(2)} @ ${new Date(hoveredPosition.exitTime).toLocaleTimeString()}` } : null,
            { label: 'Qty', val: `${hoveredPosition.qty} shares` },
          ].filter(Boolean).map(({ label, val }) => (
            <div key={label} style={{ fontSize: 9, color: C.body, marginBottom: 3 }}>
              <span style={{ color: C.meta }}>{label}: </span>{val}
            </div>
          ))}
          <div style={{ fontSize: 11, color: hoveredPosition.pnl >= 0 ? C.green : C.red, fontWeight: 500, marginTop: 6 }}>
            P&L: {hoveredPosition.pnl >= 0 ? '+' : ''}${Number(hoveredPosition.pnl || 0).toFixed(2)}
          </div>
          <div style={{ fontSize: 9, color: C.meta, marginTop: 6, opacity: 0.7 }}>Click to view position details</div>
        </Tip>
      )}
    </div>
  );
};

export default EvaluationTimeline;